import styled from "@emotion/styled"
import { Icon } from "components/icon/Icon"
import Image from "components/media/ImageCLD"
import { useDictionary } from "contexts/Dictionary"
import getMediaQuery from "css/breakpoints"
import { buttonIcon, buttonReset, translucentDark } from "css/buttons"
import { bold, legend120 } from "css/text"
import { useEffect, useId, useRef, useState } from "react"

const Root = styled.section`
  width: 100vw;
  height: 100vh;
  position: relative;
  font-size: 0;
`

const OpacityWrapper = styled.li`
  width: 100vw;
  height: 100vh;
  flex-shrink: 0;
  scroll-snap-align: start;

  /* will-change: opacity;
  transition: opacity 0.5s ease-in-out;
  opacity: 0;

  &.visible {
    opacity: 1;
  } */
`

const ImageContainer = styled.ul`
  position: absolute;
  inset: 0;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  white-space: nowrap;
  display: flex;

  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ArrowLeft = styled.button`
  ${buttonIcon}
  ${translucentDark}
  position: absolute;
  top: 50%;
  inset-inline-start: calc(var(--outer-margin) / 2);
  transform: translateX(-50%) translateY(-50%) rotate(180deg);
  will-change: opacity;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  display: none;

  ${getMediaQuery("m")} {
    display: block;
  }

  // revert arrow if rtl
  [dir="rtl"] & {
    transform: translateX(-50%) translateY(-50%) rotate(180deg) scaleX(-1);
  }
`

const ArrowRight = styled.button`
  ${buttonIcon}
  ${translucentDark}
  position: absolute;
  top: 50%;
  inset-inline-end: calc(var(--outer-margin) / 2);
  transform: translateX(50%) translateY(-50%);
  will-change: opacity;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  display: none;

  ${getMediaQuery("m")} {
    display: block;
  }

  // revert arrow if rtl
  [dir="rtl"] & {
    transform: translateX(50%) translateY(-50%) scaleX(-1);
  }
`

const PaginationNav = styled.nav`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 2.5rem;

  html.prefers-contrast & {
    color: black !important;
    position: relative;

    ::after {
      background: white;
      content: "";
      height: calc(100% + 30px);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 20px);
      z-index: -1;
    }
  }

  ${getMediaQuery("m")} {
    bottom: 5rem;
  }

  > ul {
    display: flex;
  }
`

const PaginationDot = styled.button`
  ${buttonReset}
  height: 0.25rem;
  width: 2.5rem;
  margin: 0 6px;
  border-radius: 3px;
  background-color: rgb(var(--grey));
  will-change: width background-color;
  transition: width 0.2s ease-in-out, background-color 0.2s ease-in-out;
  cursor: pointer;
  position: relative;

  html.prefers-contrast & {
    background: black;
  }

  &.active {
    background-color: rgb(var(--pure-white));
    width: 6.25rem;
    html.prefers-contrast & {
      background: black;
    }
  }

  &:after {
    content: "";
    position: absolute;
    top: -12px;
    bottom: -12px;
    left: -4px;
    right: -4px;
  }
`

const Caption = styled.p`
  ${legend120}
  ${bold}
  position: absolute;
  bottom: 6vh;
  left: 3vw;
  color: rgb(var(--pure-white));
`

export default function Gallery(props) {
  const dictionary = useDictionary()
  const { slides } = props
  const [currentPicture, setCurrentPicture] = useState(0)
  const [initialX, setInitialX] = useState(0)
  const container = useRef()
  const arrowLeft = useRef()
  const arrowRight = useRef()

  const handleScroll = () => {
    // trigger the scroll event only when the scroll is finished
    window.galleryTimeoutId && clearTimeout(window.galleryTimeoutId)
    window.galleryTimeoutId = setTimeout(() => {
      const { scrollLeft } = container.current
      const { innerWidth } = window
      const current = Math.round(scrollLeft / innerWidth)
      setCurrentPicture(Math.abs(current))
    }, 100)
  }

  // function that displays the arrows if the mouse is moving with debounce
  const showArrows = () => {
    // if the current picture is the first or the last, don't show the arrows and disable pointer events
    if (currentPicture !== 0) {
      arrowLeft.current.style.opacity = 1
      arrowLeft.current.style.pointerEvents = "all"
    }
    if (currentPicture !== slides.length - 1) {
      arrowRight.current.style.opacity = 1
      arrowRight.current.style.pointerEvents = "all"
    }
    window.galleryTimeoutId && clearTimeout(window.galleryTimeoutId)
    window.galleryTimeoutId = setTimeout(() => {
      arrowLeft.current.style.opacity = 0
      arrowLeft.current.style.pointerEvents = "none"
      arrowRight.current.style.opacity = 0
      arrowRight.current.style.pointerEvents = "none"
    }, 2000)
  }

  const scrollBy = pictureIndex => {
    // get current li offsetleft according to index
    const { offsetLeft } = container.current.children[pictureIndex]
    console.log("change position", pictureIndex, offsetLeft)

    container.current.scrollTo({
      top: 0,
      left: offsetLeft,
      behavior: "smooth",
    })
  }

  const nextPicture = () => {
    if (currentPicture < slides.length - 1) {
      const nextPicture = Math.abs(currentPicture + 1)
      setCurrentPicture(nextPicture)
      // scrollBy(nextPicture)
    }
  }

  const prevPicture = () => {
    if (currentPicture > 0) {
      const prevPicture = Math.abs(currentPicture - 1)
      setCurrentPicture(prevPicture)
      // scrollBy(prevPicture)
    }
  }

  const updatePicture = index => {
    // get current li offsetleft according to index
    const { offsetLeft } = container.current.children[Math.abs(index)]

    container.current.scrollTo({
      top: 0,
      left: offsetLeft,
      behavior: "smooth",
    })

    // setCurrentPicture(Math.abs(index))
  }

  useEffect(() => {
    if (slides) {
      // hide arrows when gallery is at its limit
      if (currentPicture === 0) {
        arrowLeft.current.style.opacity = 0
      } else {
        arrowLeft.current.style.opacity = 1
      }
      if (currentPicture === slides.length - 1) {
        arrowRight.current.style.opacity = 0
      } else {
        arrowRight.current.style.opacity = 1
      }
    }
  }, [slides, currentPicture])

  useEffect(() => {
    if (container?.current) {
      const { offsetLeft } = container.current.children[Math.abs(currentPicture)]

      container.current.scrollTo({
        top: 0,
        left: offsetLeft,
        behavior: "smooth",
      })
    }
  }, [currentPicture])

  return (
    // <Root onMouseMove={showArrows} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchSlide}>
    <Root onMouseMove={showArrows} className='dark-theme'>
      <ImageContainer ref={container} onScroll={handleScroll} aria-label={dictionary.galleryImagesAriaLabel()}>
        {slides?.map(({ picture_cld: picture }, index) => (
          <OpacityWrapper key={`pic-${index}`} className={currentPicture === index ? "visible" : ""}>
            {picture.uid ? <Image {...picture} key={picture.uid} /> : <Image {...picture} key={index} />}
          </OpacityWrapper>
        ))}
      </ImageContainer>
      {slides && <Caption>{slides[currentPicture]?.caption}</Caption>}
      <ArrowLeft tabIndex='-1' aria-hidden='true' onClick={prevPicture} ref={arrowLeft}>
        <Icon type='moveNext' />
      </ArrowLeft>
      <ArrowRight tabIndex='-1' aria-hidden='true' onClick={nextPicture} ref={arrowRight}>
        <Icon type='moveNext' />
      </ArrowRight>
      <PaginationNav aria-label={dictionary.galleryPaginationAriaLabel()}>
        <ul>
          {slides?.map((slide, index) => (
            <li key={`page-dot-${index}`}>
              <PaginationDot
                className={currentPicture === index ? "active" : ""}
                aria-current={currentPicture === index ? "true" : "false"}
                aria-label={`See image ${index + 1}`}
                onClick={() => setCurrentPicture(index)}
              />
            </li>
          ))}
        </ul>
      </PaginationNav>
    </Root>
  )
}
